import { ArrowLeft, ArrowRight, LoaderCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface StepNavigationProps {
  onNext?: () => Promise<void> | void
  onBack?: () => void
  errorMessages?: string[]
  nextText?: string
  backText?: string
  isLoading?: boolean
  loadingText?: string
  isNextDisabled?: boolean
  className?: string
  wrapperClassName?: string
  /** Extra classes for the error list. A sticky caller passes a height cap here. */
  errorsClassName?: string
  showArrows?: boolean
}

export function StepNavigation({
  onNext,
  onBack,
  errorMessages,
  nextText = 'Next',
  backText = 'Back',
  isLoading = false,
  loadingText = 'Processing…',
  isNextDisabled,
  className,
  wrapperClassName,
  errorsClassName,
  showArrows = false,
}: StepNavigationProps) {
  return (
    <div className={cn('flex w-full flex-col gap-5', wrapperClassName)}>
      {/* Error Messages. `errorsClassName` is how a STICKY caller caps this list:
          pinned to the bottom with an opaque background, an unbounded list grows
          upward over the page and can cover a control (it swallowed clicks on the
          onboarding "Add address" button). Non-sticky callers scroll with the page
          and must keep the list unbounded, so there is no default cap here. */}
      {errorMessages && errorMessages.length > 0 && (
        <div
          className={cn(
            'space-y-1 rounded-control border border-danger-border bg-danger-tint p-3 text-sm font-normal text-danger',
            errorsClassName,
          )}
          role="alert"
        >
          {errorMessages.map((error) => (
            <p key={error}>• {error}</p>
          ))}
        </div>
      )}
      {/* Navigation */}
      <div className={cn('flex items-center justify-between', className)}>
        {onBack && (
          <Button variant="outline" onClick={onBack} disabled={isLoading}>
            {showArrows && <ArrowLeft className="h-4 w-4" />}
            {backText}
          </Button>
        )}
        {onNext && (
          <Button onClick={onNext} disabled={isLoading || isNextDisabled}>
            {isLoading && <LoaderCircle className="animate-spin" />}
            {isLoading ? loadingText : nextText}
            {showArrows && !isLoading && <ArrowRight className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  )
}
